import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import TopNav from './TopNav';
import AppGrid from './AppGrid';
import AppDetailModal from './AppDetailModal';
import TypeLegend from './TypeLegend';
import { findAppByNavLink } from './appTypes';
import '../theme/tokens.css';
import './StorePage.css';

/** Query parameter the open app's `navLink` is carried in. */
const APP_PARAM = 'app';

function filterByName(apps, query) {
  const needle = query.trim().toLowerCase();
  if (!needle) return apps;
  return apps.filter((app) => app.name.toLowerCase().includes(needle));
}

/**
 * Shared shell for the four store pages: sticky nav, page header, card grid, and
 * the detail modal layered over them.
 *
 * Each page hands in its own already-scoped `apps` list (Home dedupes by
 * affiliate and decorates entries with `typeKeys`; the per-type pages pass their
 * one list straight through). The search query lives here, alongside the list it
 * filters, so a tab change — which remounts this component — clears it.
 *
 * Which app the modal shows is URL state rather than component state: cards are
 * real links to `?app=<navLink>`, so a shared link reopens the same modal, and
 * browser back closes it. `apps` is only what the grid shows, so the modal
 * resolves the open app out of the whole `comps` payload — a pill can switch to
 * an affiliate's app of a type this page does not list.
 *
 * The type-dot key is keyed off the cards themselves rather than off which page
 * this is: it shows when the list carries `typeKeys`, which is exactly when the
 * cards can draw dots.
 */
export default function StorePage({ apps, title, comps }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();

  const visibleApps = useMemo(() => filterByName(apps, searchQuery), [apps, searchQuery]);

  // Read off the full list, not `visibleApps`, so narrowing a search down to one
  // single-type company does not pull the key out from under the dots.
  const showsTypeDots = apps.some((app) => (app.typeKeys || []).length > 0);

  // A `?app=` naming something the payload no longer has resolves to nothing,
  // which shows the plain grid rather than an error.
  const selectedApp = findAppByNavLink(comps, searchParams.get(APP_PARAM));

  function closeModal() {
    const next = new URLSearchParams(searchParams);
    next.delete(APP_PARAM);
    // Replace, so the entry the modal was opened from is the one back returns to
    // rather than one that would reopen it.
    setSearchParams(next, { replace: true });
  }

  return (
    <div className="itcStorePage">
      <TopNav searchQuery={searchQuery} onSearchChange={setSearchQuery} />

      <main className="itcStoreMain">
        <header className="itcStoreHeader">
          <p className="itcStoreEyebrow">ITCurves App Store</p>
          <h1 className="itcStoreTitle">{title}</h1>
          {showsTypeDots && <TypeLegend />}
        </header>

        <AppGrid apps={visibleApps} searchQuery={searchQuery} />
      </main>

      {selectedApp && (
        <AppDetailModal
          app={selectedApp}
          comps={comps}
          onClose={closeModal}
        />
      )}
    </div>
  );
}

StorePage.propTypes = {
  apps: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      navLink: PropTypes.string.isRequired,
      typeKeys: PropTypes.arrayOf(PropTypes.string),
    })
  ).isRequired,
  title: PropTypes.string.isRequired,
  comps: PropTypes.object,
};
